import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  List,
  Divider,
  Tooltip,
} from "@mui/material";
import { Add, ArrowForward, Edit, AccountTree } from "@mui/icons-material";
import { mutate } from "swr";
import axios from "axios";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import {
  createEntity,
  deleteEntity,
  updateEntity,
} from "../../../context/GlobalContext";
import Combobox from "../../../components/common/Combobox";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import { getClearStatusChangeModel } from "../../../common/EntitiesManagement";
import ErrorMessage from "../../../components/common/ErrorMessage";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ScrollablePageTab from "../../dynamicForm/components/ScrollablePageTab";
import StatusChangeConfigInfo from "./StatusChangeConfigInfo";
import StatusChangeGraph from "./StatusChangeGraph";
import NotificationItem from "../../../components/common/NotificationItem";
import ModalBackground from "../../../components/common/ModalBackground";
import { MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../../common/utils";
import ExpandableView from "../../../components/common/ExpandableView";
import CreatedByText from "../../../components/common/CreatedByText";

export default function EditWorkflow() {
  const {
    workflowModel,
    setWorkflowModel,
    STATUS_ENDPOINT,
    dynamicFormData,
    statusChangeModel,
    setStatusChangeModel,
    STATUS_CHANGE_SETUP_API,
    pageIndex,
    setPageIndex,
    config,
    toggleVisibleModalConfirm,
  } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [statusChangesByWorkFlowId, setStatusChangeWorkFlowId] = useState([]);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [selectedTab, setSelectedtab] = useState("0");
  const listRef = useRef(null);

  const getEventStatus = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${STATUS_ENDPOINT}`, requestConfig);

    setStatusData(data?.data);
  };

  useEffect(() => {
    getEventStatus();
  }, []);

  const getStatusChangesByWorkFlowId = async () => {
    const data = await axios.get(
      `${STATUS_CHANGE_SETUP_API}/workflow/${workflowModel?.Id}`,
      config
    );

    setStatusChangeWorkFlowId(data.data ?? []);
  };

  useEffect(() => {
    getStatusChangesByWorkFlowId();
  }, [workflowModel?.Id]);

  useEffect(() => {
    if (workflowModel.Description) {
      setDescriptionCount(workflowModel.Description.length);
    }
  }, [workflowModel?.Description]);

  const validateOperationResult = (result, isDelete) => {
    setStatusChangeModel({
      ...statusChangeModel,
      InitialStatusId: 0,
      InitialStatus: null,
      FinalStatusId: 0,
      FinalStatus: null,
      DynamicFormId: 0,
      DynamicForm: null,
    });

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    setShowStatusChangeModal(false);
    mutate([STATUS_CHANGE_SETUP_API, config]);
    if (isDelete) {
      if (pageIndex !== 0 && workflowModel.Data.length === 1) {
        setPageIndex(pageIndex - 1);
      }
    }
  };

  const handleChange = (e) => {
    setWorkflowModel({
      ...workflowModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleInitialStatusChange = (value) => {
    if (!value) {
      setStatusChangeModel({
        ...statusChangeModel,
        InitialStatusId: 0,
        InitialStatus: null,
      });
      return;
    }

    setStatusChangeModel({
      ...statusChangeModel,
      InitialStatusId: value.Id,
      InitialStatus: value,
    });
  };

  const handleCheckedChanged = (e) => {
    setStatusChangeModel({
      ...statusChangeModel,
      [e.target.name]: e.target.checked,
    });
  };

  const handleDynamicFormChange = (value) => {
    if (!value) {
      setStatusChangeModel({
        ...statusChangeModel,
        DynamicFormId: 0,
        DynamicForm: null,
      });
      return;
    }

    setStatusChangeModel({
      ...statusChangeModel,
      DynamicFormId: value.Id,
      DynamicForm: value,
    });
  };

  const handleFinalStatusChange = (value) => {
    if (!value) {
      setStatusChangeModel({
        ...statusChangeModel,
        FinalStatusId: 0,
        FinalStatus: null,
      });
      return;
    }

    setStatusChangeModel({
      ...statusChangeModel,
      FinalStatusId: value.Id,
      FinalStatus: value,
    });
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${STATUS_CHANGE_SETUP_API}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${STATUS_CHANGE_SETUP_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    getStatusChangesByWorkFlowId();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${STATUS_CHANGE_SETUP_API}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleModalSubmit = async () => {
    if (
      !statusChangeModel?.InitialStatusId ||
      !statusChangeModel?.FinalStatusId
    )
      return;

    if (showEdit) {
      await handleUpdateRequest(statusChangeModel);
      setStatusChangeModel(getClearStatusChangeModel());
      setShowEdit(false);
    } else {
      await handleCreateRequest({
        ...statusChangeModel,
        WorkFlowId: workflowModel?.Id,
      });
    }
    getStatusChangesByWorkFlowId();
  };

  const handleOpenAdd = () => {
    setStatusChangeModel({ IsAdministrative: false });
    setShowEdit(false);
    setShowStatusChangeModal(true);
  };

  const handleOpenEdit = (statusChangeObj) => {
    setStatusChangeModel({
      ...statusChangeObj,
      WorkFlowId: workflowModel?.Id,
      InitialStatusId: statusChangeObj.InitialStatus.Id,
      FinalStatusId: statusChangeObj?.FinalStatus?.Id,
      IsAdministrative: statusChangeObj?.IsAdministrative,
    });
    setShowEdit(true);
    setShowStatusChangeModal(true);
  };

  const disableSaveChangesForStatusChange = () =>
    !statusChangeModel?.FinalStatus || !statusChangeModel?.InitialStatus;

  return (
    <div ref={listRef}>
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={`${localize.status_change} : ${
            statusChangeModel.InitialStatus?.Name
          } -> ${
            statusChangeModel.FinalStatus?.Name ??
            statusChangeModel.InitialStatus?.Name
          }`}
          entityType={localize.status}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setStatusChangeModel(getClearStatusChangeModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(statusChangeModel.Id);
          }}
        />
      )}

      {showStatusChangeModal && (
        <ModalBackground
          isDrawerMode
          showModal={showStatusChangeModal}
          setShowModal={setShowStatusChangeModal}
          title={showEdit ? localize.edit : localize.add_status_change}
          okButtonText={showEdit ? localize.edit : localize.add}
          validators={disableSaveChangesForStatusChange()}
          onClickToCancel={() => {
            setShowStatusChangeModal(false);
            setShowEdit(false);
            setStatusChangeModel(getClearStatusChangeModel());
          }}
          onClickToSubmit={handleModalSubmit}
          Component={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <Combobox
                isRequired
                id="InitialStatus"
                fieldLabel={localize.initial_status}
                fieldOptions={statusData?.Data ?? []}
                fieldValue={statusChangeModel?.InitialStatus ?? null}
                handleValueChange={handleInitialStatusChange}
                popperFullWidth={PopperFullWidth}
              />

              <Combobox
                isRequired
                id="FinalStatus"
                fieldLabel={localize.final_status}
                fieldOptions={statusData?.Data ?? []}
                fieldValue={statusChangeModel?.FinalStatus ?? null}
                handleValueChange={handleFinalStatusChange}
                popperFullWidth={PopperFullWidth}
                disabled={
                  !statusChangeModel.FinalStatus &&
                  statusChangeModel.Id > 0
                }
              />

              <Combobox
                id="Dynamicform"
                fieldLabel={localize.dynamic_form}
                fieldOptions={dynamicFormData?.Data ?? []}
                fieldValue={statusChangeModel?.DynamicForm ?? null}
                handleValueChange={handleDynamicFormChange}
                popperFullWidth={PopperFullWidth}
                disabled={
                  !statusChangeModel.FinalStatus &&
                  statusChangeModel.Id > 0
                }
              />

              <FormControlLabel
                size="small"
                name="IsAdministrative"
                control={
                  <Checkbox
                    size="small"
                    name="IsAdministrative"
                    checked={statusChangeModel.IsAdministrative ?? false}
                    onChange={handleCheckedChanged}
                  />
                }
                label={
                  <span style={{ fontSize: "0.8em" }}>
                    {localize.status_change_only_available_on_web}
                  </span>
                }
              />

              {statusChangeModel?.DynamicForm && (
                <Alert severity="warning" className="w-full">
                  <label className="text-xs">
                    {localize.info_dynamic_form_on_status_change}
                  </label>
                </Alert>
              )}
            </Box>
          }
        />
      )}

      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <Box className="w-full mt-2">
                <div className="mb-6 w-full">
                  <FormControl required fullWidth>
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="Name"
                      label={localize.name}
                      variant="outlined"
                      onChange={handleChange}
                      value={workflowModel?.Name ?? null}
                    />
                  </FormControl>
                </div>

                <div className="flex flex-wrap mb-1">
                  <FormControl required fullWidth>
                    <TextField
                      InputLabelProps={getInputLabelProps()}
                      inputProps={{
                        style: {
                          fontSize: "0.8em",
                        },
                        maxLength: maximumCharactersForDescription,
                      }}
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      id="outlined-basic"
                      name="Description"
                      label={localize.description}
                      variant="outlined"
                      onChange={handleChange}
                      value={workflowModel?.Description ?? null}
                      helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                    />
                  </FormControl>
                </div>
              </Box>
            ),
          },
          {
            Name: localize.status_changes,
            LongContent: true,
            Component: (
              <Box className="w-full">
                <div className="flex flex-row-reverse justify-end items-center w-full mt-2">
                  <div className="flex gap-1">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      className="app_general_button_data app_success_button btn-sm"
                      onClick={handleOpenAdd}
                    >
                      <span className="mr-2">{localize.add_status_change}</span>
                      <Add className="text-xs" />
                    </Button>

                    <Tooltip title={localize.graph} arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 0, px: 1 }}
                        onClick={() => setShowGraph(true)}
                      >
                        <AccountTree sx={{ fontSize: 18 }} />
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                <Box className="my-2 w-full">
                  <Divider />
                </Box>

                <Box sx={{ height: "500px", marginTop: "10px" }}>
                  <List
                    className="my-2 flex flex-col gap-2"
                    sx={{ overFlowY: "auto" }}
                  >
                    {statusChangesByWorkFlowId?.Data &&
                    statusChangesByWorkFlowId?.Data.length > 0 ? (
                      statusChangesByWorkFlowId?.Data?.map(
                        (statusChangeObj) => (
                          <StatusChangeConfigInfo
                            key={statusChangeObj.Id}
                            statusChangeObj={statusChangeObj}
                            onEditButtonClick={() => handleOpenEdit(statusChangeObj)}
                            onDeleteButtonclick={() => {
                              toggleVisibleModalConfirm();
                              setStatusChangeModel(statusChangeObj);
                              setShowDeleteModal(true);
                            }}
                            hideDeleteButton={!statusChangeObj.FinalStatus}
                          />
                        )
                      )
                    ) : (
                      <NotificationItem
                        notification={{
                          type: 0,
                          message: localize.no_status_changes,
                        }}
                      />
                    )}
                  </List>
                </Box>
              </Box>
            ),
          },
        ]}
      />

      {showGraph && (
        <StatusChangeGraph
          statusChanges={statusChangesByWorkFlowId?.Data}
          onClose={() => setShowGraph(false)}
        />
      )}

      <CreatedByText entity={workflowModel} />
    </div>
  );
}
